/**
 * Global fetch wrapper for the Loaf Worker API.
 *
 * All requests carry `credentials: 'include'` so the httpOnly `session`
 * cookie (set by POST /auth/web) is sent cross-origin to
 * api.loaf.binodswain.dev. On 401 the SPA redirects to /auth with a
 * "session expired" message — no silent failures.
 */

const DEFAULT_BASE_URL = 'https://api.loaf.binodswain.dev';

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL;

/** Redirect target query param used to surface auth-failure context. */
export const SESSION_EXPIRED_QUERY = 'expired';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function redirectToAuth(expired: boolean): void {
  // Avoid redirect loops when already on /auth.
  if (window.location.pathname.startsWith('/auth')) {
    return;
  }
  const url = expired ? '/auth?expired=1' : '/auth';
  window.location.assign(url);
}

interface FetchOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip the 401 redirect (e.g. the auth call itself). Defaults to false. */
  skipAuthRedirect?: boolean;
}

/**
 * Core request helper. Throws `ApiError` on non-2xx. On 401 (and not
 * `skipAuthRedirect`) redirects to /auth with the session-expired flag.
 */
export async function request<T>(
  path: string,
  opts: FetchOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers, signal, skipAuthRedirect = false } =
    opts;

  const init: RequestInit = {
    method,
    credentials: 'include',
    signal,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const res = await fetch(url, init);

  if (res.status === 401 && !skipAuthRedirect) {
    redirectToAuth(true);
    // Throw so callers don't try to parse the body as data.
    throw new ApiError(401, 'Session expired', null);
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const parsed: unknown = isJson || res.status !== 204 ? await res.json() : null;

  if (!res.ok) {
    let message: string = res.statusText || `Request failed (${res.status})`;
    if (isJson && parsed && typeof parsed === 'object' && 'error' in parsed) {
      const err = (parsed as Record<string, unknown>).error;
      if (typeof err === 'string' && err) message = err;
    }
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

export const apiGet = <T>(path: string, opts?: Omit<FetchOptions, 'method' | 'body'>) =>
  request<T>(path, { ...opts, method: 'GET' });

export const apiPost = <T>(
  path: string,
  body?: unknown,
  opts?: Omit<FetchOptions, 'method'>,
) => request<T>(path, { ...opts, method: 'POST', body });

export const apiPatch = <T>(
  path: string,
  body?: unknown,
  opts?: Omit<FetchOptions, 'method'>,
) => request<T>(path, { ...opts, method: 'PATCH', body });

export const apiDelete = <T>(path: string, opts?: Omit<FetchOptions, 'method' | 'body'>) =>
  request<T>(path, { ...opts, method: 'DELETE' });
